import React, { useEffect, useState } from 'react'
import map from 'lodash/map'
import ScrollingLoader from '@SiteComponents/ScrollingLoader'
import SpeakerVoiceRow from './SpeakerVoiceRow'

import {
  ISpeakerVoice,
} from '@SiteModules/SpeakerVoice/types'

const COLUMN_COUNT = 8

import './SpeakerVoicesTableList.scss'

interface SpeakerVoicesTableListProps {
  readonly speakerVoices: ISpeakerVoice[]
  readonly loading: boolean
  readonly hasLoadedAllSpeakerVoices: boolean
  readonly selectedSpeakerVoiceIds: string[]
  changeSelectedSpeakerVoices(e: React.ChangeEvent<HTMLInputElement>, speakerVoiceId: string): void
  openRemoveSpeakerVoicePopup(speakerVoiceId: string): void
  handleSidePanelOpen(speakerVoice: ISpeakerVoice): void
}

const LOADING_INDICATOR_DELAY = 180

const SpeakerVoicesTableList: React.FC<SpeakerVoicesTableListProps> = props => {
  const {
    speakerVoices,
    loading,
    hasLoadedAllSpeakerVoices,
    selectedSpeakerVoiceIds,
  } = props

  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false)

  useEffect(() => {
    if (!loading) {
      setShowLoadingIndicator(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowLoadingIndicator(true)
    }, LOADING_INDICATOR_DELAY)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [loading])

  const showFooter = !loading && hasLoadedAllSpeakerVoices

  const renderTableHeader = () => {
    return (
      <tr>
        <th className="SpeakerVoicesTableList__check"/>
        <th className="SpeakerVoicesTableList__thumbnail-name">
          Name
        </th>
        <th className="SpeakerVoicesTableList__description">
          Description
        </th>
        <th className="SpeakerVoicesTableList__language">
          Language
        </th>
        <th className="SpeakerVoicesTableList__gender">
          Gender
        </th>
        <th className="SpeakerVoicesTableList__edited">
          Edited
        </th>
        <th className="SpeakerVoicesTableList__private">
          Private
        </th>
        <th className="SpeakerVoicesTableList__date-of-creation">
          Date of creation
        </th>
        <th className="SpeakerVoicesTableList__more"/>
      </tr>
    )
  }

  const tableRows = map(speakerVoices, (speakerVoice, index) => {
    const selected = selectedSpeakerVoiceIds.includes(speakerVoice.id)

    return (
      <SpeakerVoiceRow
        key={index}
        index={index}
        speakerVoice={speakerVoice}
        selected={selected}
        changeSelectedSpeakerVoices={props.changeSelectedSpeakerVoices}
        openRemoveSpeakerVoicePopup={props.openRemoveSpeakerVoicePopup}
        handleSidePanelOpen={props.handleSidePanelOpen}
      />
    )
  })

  const renderLoader = () => {
    return (
      <tr>
        <td colSpan={COLUMN_COUNT}>
          <ScrollingLoader scale={0.5}/>
        </td>
      </tr>
    )
  }

  const renderFooter = () => {
    return (
      <React.Fragment>
        <tr className="SpeakerVoicesTableList__line-container">
          <td colSpan={COLUMN_COUNT} className="SpeakerVoicesTableList__line"/>
        </tr>
        <tr>
          <td colSpan={COLUMN_COUNT} className="SpeakerVoicesTableList__footer">
            You've reached the end of the list.
          </td>
        </tr>
      </React.Fragment>
    )
  }

  return (
    <div className="SpeakerVoicesTableList">
      <table>
        <thead>
          {renderTableHeader()}
        </thead>
        <tbody className="SpeakerVoicesTableList__body">
          {tableRows}
          {showLoadingIndicator && renderLoader()}
          {showFooter && renderFooter()}
        </tbody>
      </table>
    </div>
  )
}

export default SpeakerVoicesTableList
